import { useCallback, useEffect } from 'react';
import { DEFAULT_AVATAR } from '../../../../../shared/utils/matrix/matrixRoomUtils';
import {
  buildDeductCallCreditPayload,
  formatCallDuration,
  getCallRequestPricingDetails,
  hangupPartyForCallBilling,
  MIN_BILLED_CALL_DURATION_SEC,
  resolvePostCallBillingDurationSec,
} from '../utils/callFormatUtils';
import { getCallKindFromCallUiType } from '../constant';

export const useCallSummaryRedirect = ({
  activeCall,
  billingCreatorId,
  billingCreatorProfile,
  callSessionTipUsdRef,
  callUiType,
  durationAccumulatedSecRef,
  durationStartAtRef,
  hasRedirectedRef,
  isCreator,
  lastEndedReasonRef,
  lastHangupPartyRef,
  navigate,
  opponentAvatar,
  opponentDisplayName,
  redirectAfterCallRef,
  resolvedUserId,
  roomId,
  /** Mirrors {@link useCallDurationTimer}'s `callDurationSec` so summary/billing match what the user saw if refs lag during teardown. */
  latestDisplayedCallDurationSecRef,
  /** Creator profile used to snap flat-rate insufficient-credit duration to block boundaries. */
  postCallBillingCreatorInfo,
}) => {
  const redirectAfterCall = useCallback(() => {
    if (!navigate?.toCallSummary) return;

    // Prefer provider-tracked values, but fall back to SDK call fields
    // to avoid races between hangup events and React effects.
    const endedReason = lastEndedReasonRef.current || activeCall?.hangupReason || null;
    const hangupParty = lastHangupPartyRef.current || activeCall?.hangupParty || null;

    const callKind = getCallKindFromCallUiType(callUiType);
    const pricingProfile = postCallBillingCreatorInfo || billingCreatorProfile || null;
    const durationBillingContext = pricingProfile
      ? { creatorInfo: pricingProfile, callKind, endedReason }
      : null;

    const durationSeconds = resolvePostCallBillingDurationSec(
      durationAccumulatedSecRef,
      durationStartAtRef,
      latestDisplayedCallDurationSecRef,
      durationBillingContext
    );
    const durationLabel = formatCallDuration(durationSeconds);
    let billedCredits = undefined;
    let callPricingSnapshot = null;
    if (isCreator && billingCreatorId && billingCreatorProfile) {
      const pricing = getCallRequestPricingDetails(billingCreatorProfile, callKind);
      if (pricing?.ok) {
        callPricingSnapshot = {
          pricingModel: pricing.pricingModel,
          usdPerMinute: pricing.usdPerMinute,
          amountUsd: pricing.amountUsd,
          minDurationMins: pricing.minDurationMins,
        };
      }
      if (durationSeconds < MIN_BILLED_CALL_DURATION_SEC) {
        billedCredits = 0;
      } else {
        const hangupForBilling = hangupPartyForCallBilling(isCreator, hangupParty);
        const payload = buildDeductCallCreditPayload(
          billingCreatorId,
          billingCreatorProfile,
          callKind,
          durationSeconds,
          { hangupParty: hangupForBilling, endedReason }
        );
        if (payload?.credit != null) {
          const n = Number(payload.credit);
          billedCredits = Number.isFinite(n) ? n : 0;
        } else {
          billedCredits = 0;
        }
      }
    }

    const tipUsd =
      isCreator && callSessionTipUsdRef
        ? Math.max(0, Number(callSessionTipUsdRef.current) || 0)
        : 0;

    const summaryPayload = {
      isCreator,
      opponentDisplayName: opponentDisplayName || 'Unknown',
      opponentAvatar: opponentAvatar || DEFAULT_AVATAR,
      roomId: roomId || null,
      callDurationSec: durationSeconds,
      callDuration: durationLabel,
      endedReason,
      hangupParty,
      userId: resolvedUserId || null,
      callUiType,
      // Duration already billing-adjusted — CallSummary must not re-snap with a different profile.
      billingDurationFinal: true,
      ...(isCreator
        ? { billedCredits: billedCredits ?? 0, tipUsd, callPricing: callPricingSnapshot }
        : {}),
    };

    // Embedded summary on chat (blur backdrop) when the DM peer id is known; fallback is standalone /call/summary.
    if (resolvedUserId && typeof navigate?.toChat === 'function') {
      navigate.toChat(resolvedUserId, {
        state: {
          ...(roomId ? { preferredRoomId: roomId } : {}),
          callSummaryPopup: summaryPayload,
        },
      });
      return;
    }

    navigate.toCallSummary({
      state: summaryPayload,
      query: {
        isCreator: isCreator ? '1' : '0',
        roomId: roomId || '',
        callDurationSec: durationSeconds,
        callUiType,
        endedReason: endedReason || '',
        hangupParty: hangupParty || '',
        ...(isCreator
          ? { billedCredits: String(billedCredits ?? 0), tipUsd: String(tipUsd) }
          : {}),
      },
    });
  }, [
    activeCall,
    billingCreatorId,
    billingCreatorProfile,
    callSessionTipUsdRef,
    callUiType,
    durationAccumulatedSecRef,
    durationStartAtRef,
    isCreator,
    lastEndedReasonRef,
    lastHangupPartyRef,
    navigate,
    opponentAvatar,
    opponentDisplayName,
    resolvedUserId,
    roomId,
    latestDisplayedCallDurationSecRef,
    postCallBillingCreatorInfo,
  ]);

  const triggerRedirectOnce = useCallback(() => {
    if (hasRedirectedRef.current) return;
    hasRedirectedRef.current = true;
    redirectAfterCall();
  }, [hasRedirectedRef, redirectAfterCall]);

  useEffect(() => {
    redirectAfterCallRef.current = triggerRedirectOnce;
  }, [redirectAfterCallRef, triggerRedirectOnce]);

  return { triggerRedirectOnce };
};
